import { twJoin } from "tailwind-merge"
import React, { CSSProperties, ReactNode, useEffect, useMemo, useRef, useState } from "react"
import { Fade, SlideshowRef } from "react-slideshow-image"
import Image from "next/image"
import Link from "next/link"
import Icon from "@components/UI/Icon"
import slide0 from "@images/home-slides/features/slide_0.webp"
import slide0Mobile from "@images/home-slides/features/slide_0_mobile.webp"
import slide1 from "@images/home-slides/features/slide_1.webp"
import slide1Mobile from "@images/home-slides/features/slide_1_mobile.webp"
import slide2 from "@images/home-slides/features/slide_2.webp"
import slide2Mobile from "@images/home-slides/features/slide_2_mobile.webp"
import slide3 from "@images/home-slides/features/slide_3.webp"
import slide3Mobile from "@images/home-slides/features/slide_3_mobile.webp"
import slide4 from "@images/home-slides/features/slide_4.webp"
import slide4Mobile from "@images/home-slides/features/slide_4_mobile.webp"
import slide5 from "@images/home-slides/features/slide_5.webp"
import slide5Mobile from "@images/home-slides/features/slide_5_mobile.webp"
import slide6 from "@images/home-slides/features/slide_6.webp"
import slide6Mobile from "@images/home-slides/features/slide_6_mobile.webp"
import slide7 from "@images/home-slides/features/slide_7.webp"
import slide7Mobile from "@images/home-slides/features/slide_7_mobile.webp"
import slide8 from "@images/home-slides/features/slide_8.webp"
import slide8Mobile from "@images/home-slides/features/slide_8_mobile.webp"

export const FeatureButton = ({
  icon,
  title,
  active,
  onClick,
}: {
  icon: string
  title: ReactNode
  active: boolean
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    className={twJoin(
      "flex flex-col items-center gap-y-16p",
      "rounded-8p",
      "py-8p",
      "px-4p",
      "transition-all duration-100",
      "w-[100px]",
      "1024:w-1/3",
      "1024:min-h-[115px]",
      "1024:max-w-[130px]",
      "1440:px-8p",
      "hover:bg-[linear-gradient(95.96deg,_rgba(141,_110,_255,_0.1)_0%,_rgba(232,_57,_255,_0.1)_100.43%)]",
      active
        ? [
          "bg-[linear-gradient(95.96deg,_rgba(141,_110,_255,_0.1)_0%,_rgba(232,_57,_255,_0.1)_100.43%)]",
          "border-b border-solid border-slate-blue-300",
        ]
        : undefined
    )}
  >
    <div className={"w-24p h-24p 1440:w-32p 1440:h-32p"}>
      <Icon
        className={twJoin("[&_svg]:w-24p [&_svg]:h-24p", "1440:[&_svg]:w-32p 1440:[&_svg]:h-32p")}
        name={icon}
        defsSalt={"some"}
        color="neutral-100"
        size={32}
      />
    </div>
    <h3 className={twJoin("text-12p leading-18p", "768:text-14p font-500 text-neutral2-400")}>{title}</h3>
  </button>
)

const LearnMore = ({ className, style }: { className?: string; style: CSSProperties }) => (
  <div
    style={style}
    className={twJoin(
      className,
      "text-white",
      "whitespace-nowrap",
      "shadow-[0px_2px_16px_0px_rgba(0,_0,_0,_0.25)]",
      "bg-[linear-gradient(257.12deg,_#4491DB_-5.88%,_#EA4AF7_108.32%)]",
      "rounded-38p",
      "py-16p px-24p",
      "flex flex-row gap-8p justify-center items-center"
    )}
  >
    <span>Learn more</span>
    <Icon
      name={"arrow-right-big"}
      color={"neutral-900"}
      size={16}
      className={""}
    />
  </div>
)

export const FeatureSlide = ({ children }: { children: ReactNode }) => (
  <div className={twJoin("flex flex-col gap-40p")}>
    {/* <Link
      href={href} */}
    <div
      className={twJoin(
        "relative overflow-hidden",
        "mx-auto",
        "rounded-24p",
        "p-24p",
        "border border-solid [linear-gradient(180deg,_rgba(255,_255,_255,_0.06)_0%,_rgba(255,_255,_255,_0.08)_100%)]",
        "w-full"
      )}
    >
      {children}
    </div>
    {/* </Link> */}
  </div>
)

export const FeatureButtonsWrapper = ({ title, children }) => (
  <div
    className={twJoin(
      "w-full",
      "flex",
      "flex-col items-center gap-y-8p rounded-12p",
      "1024:bg-[rgba(0,_0,_0,_0.27)]",
      "1024:p-8p"
    )}
  >
    <h2 className={twJoin("text-12p leading-18p font-400 text-slate-blue-300 uppercase", "hidden", "1024:block")}>
      {title}
    </h2>
    <div className={twJoin("flex flex-row gap-x-4p w-full")}>{children}</div>
  </div>
)

export const FeaturesSlider = () => {
  const [
    state,
    setState,
  ] = useState<{
    currentSlide: number
    position: { x: number; y: number } | null
    smoothPosition: { x: number; y: number } | null
  }>({
    currentSlide: 0,
    position: null,
    smoothPosition: null,
  })

  useEffect(() => {
    let animationFrameId: number

    if (state.position) {
      const updatePosition = () => {
        setState((prevState) => ({
          ...prevState,
          smoothPosition: {
            x: prevState.smoothPosition
              ? prevState.smoothPosition.x + (state.position.x - prevState.smoothPosition.x) * 0.15
              : state.position.x,
            y: prevState.smoothPosition
              ? prevState.smoothPosition.y + (state.position.y - prevState.smoothPosition.y) * 0.15
              : state.position.y,
          },
        }))
        animationFrameId = requestAnimationFrame(updatePosition)
      }

      updatePosition()
    } else {
      setState((prevState) => ({
        ...prevState,
        smoothPosition: null,
      }))
    }

    return () => cancelAnimationFrame(animationFrameId)
  }, [state.position])

  const handleMouseMove = (event: React.MouseEvent) => {
    setState((prevState) => ({
      ...prevState,
      position: { x: event.clientX, y: event.clientY },
    }))
  }

  const handleMouseLeave = () => {
    setState((prevState) => ({
      ...prevState,
      position: null,
    }))
  }

  const sliderRef = useRef<SlideshowRef>(null)
  const textSliderRef = useRef<SlideshowRef>(null)

  const changeSlide = ({
    slideNumber,
    disableChangeSlide = false,
  }: {
    slideNumber: number
    disableChangeSlide?: boolean
  }) => {
    setState((prevState) => ({
      ...prevState,
      currentSlide: slideNumber,
    }))

    if (!disableChangeSlide) {
      sliderRef.current.goTo(slideNumber)
      textSliderRef.current.goTo(slideNumber)
    }
  }

  const textSlides = useMemo(
    () => [
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Set work schedules and capacity targets for every team member.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Compare planned availability with recorded hours to see where workloads
              are growing and who has room for more work.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Keep vacations and availability visible in one shared calendar.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              See who is away, when they return, and any notes they choose to share.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Organize work around clients, projects, and tasks.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Assign ownership, keep responsibilities clear, and connect every hour to the work it belongs to.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Track time automatically or add it manually across desktop, web, and browser extensions.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Every entry stays linked to projects, tasks, tags, and billing status.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Create timesheets automatically from tracked time.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Track work-related expenses, mark them as billable or non-billable, and submit timesheets and expenses for approval.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              See hours, billable work, costs, revenue, and margins in one place.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Filter by clients, projects, teams, and people to understand how work affects financial performance.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Set rates for your workspace, individual team members, or specific projects.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Traqq automatically applies the most specific rate available when calculating costs and billable amounts.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Generate invoices from tracked time, calculate totals automatically,
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              and send them directly from Traqq with customizable email templates.
            </div>
          </>
        ),
      },
      {
        description: (
          <>
            <div className={twJoin("inline", "768:block")}>
              Analyze hours, project activity, team capacity, attendance, costs, and revenue.
            </div>{" "}
            <div className={twJoin("inline", "768:block")}>
              Filter data across clients, projects, teams, people, and time periods.
            </div>
          </>
        ),
      },
    ],
    []
  )

  const imagesSlides = useMemo(
    () => [
      {
        slide: slide0,
        slideMobile: slide0Mobile,
        alt: "",
        href: "/features/team-management",
      },
      {
        slide: slide1,
        slideMobile: slide1Mobile,
        alt: "",
        href: "/features/pto-vacation-and-leave-tracker/",
      },
      {
        slide: slide2,
        slideMobile: slide2Mobile,
        alt: "",
        href: "/features/clients-projects-and-tasks/",
      },
      {
        slide: slide3,
        slideMobile: slide3Mobile,
        alt: "",
        href: "/features/time-tracking-app",
      },
      {
        slide: slide4,
        slideMobile: slide4Mobile,
        alt: "",
        href: "/features/timesheets-and-expenses/",
      },
      {
        slide: slide5,
        slideMobile: slide5Mobile,
        alt: "",
        href: "/features/dashboard/",
      },
      {
        slide: slide6,
        slideMobile: slide6Mobile,
        alt: "",
        href: "/features/pay-rates-and-invoicing",
      },
      {
        slide: slide7,
        slideMobile: slide7Mobile,
        alt: "",
        href: "/features/pay-rates-and-invoicing",
      },
      {
        slide: slide8,
        slideMobile: slide8Mobile,
        alt: "",
        href: "/features/time-reporting",
      },
    ],
    []
  )

  return (
    <div
      className={twJoin(
        "flex flex-col",
        "gap-12p",
        "768:gap-22p",
        "1024:gap-24p",
        "1440:gap-32p",
        "bg-black bg-opacity-25 rounded-8p pb-12p",
        "1024:bg-[unset]",
        "1024:pb-[unset]"
      )}
    >
      <div
        className={twJoin(
          "flex flex-row flex-nowrap justify-between",
          "w-full",
          "gap-x-4p",
          "overflow-x-auto",
          "px-12p",
          "py-12p",
          "768:pb-16p",
          "1024:px-[unset]",
          "1024:py-[unset]",
          "1024:max-w-[940px]",
          "1024:gap-x-8p",
          "1440:max-w-[1280px]",
          "mx-auto"
        )}
        style={{
          overflow: "visible auto",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
        }}
      >
        <FeatureButtonsWrapper title="Planning and availability">
          <>
            <FeatureButton
              active={state.currentSlide === 0}
              icon={"menu-attendance"}
              title={"Schedules & capacity"}
              onClick={() => changeSlide({ slideNumber: 0 })}
            />
            <FeatureButton
              active={state.currentSlide === 1}
              icon={"menu-sunset"}
              title={"Time off"}
              onClick={() => changeSlide({ slideNumber: 1 })}
            />
            <FeatureButton
              active={state.currentSlide === 2}
              icon={"menu-goal"}
              title={"Clients, projects & tasks"}
              onClick={() => changeSlide({ slideNumber: 2 })}
            />
          </>
        </FeatureButtonsWrapper>
        <FeatureButtonsWrapper title="Time tracking and work visibility">
          <>
            <FeatureButton
              active={state.currentSlide === 3}
              icon={"menu-time-tracker"}
              title={"Smart time tracking"}
              onClick={() => changeSlide({ slideNumber: 3 })}
            />

            <FeatureButton
              active={state.currentSlide === 4}
              icon={"menu-invoice"}
              title={"Timesheets & expenses"}
              onClick={() => changeSlide({ slideNumber: 4 })}
            />
            <FeatureButton
              active={state.currentSlide === 5}
              icon={"menu-activity"}
              title={"Time & financial dashboard"}
              onClick={() => changeSlide({ slideNumber: 5 })}
            />
          </>
        </FeatureButtonsWrapper>
        <FeatureButtonsWrapper title="Finance and reporting">
          <>
            <FeatureButton
              active={state.currentSlide === 6}
              icon={"menu-pay"}
              title={"Cost & billable rates"}
              onClick={() => changeSlide({ slideNumber: 6 })}
            />
            <FeatureButton
              active={state.currentSlide === 7}
              icon={"menu-employee-with-dollar-sign"}
              title=<>Client<br />invoices</>
              onClick={() => changeSlide({ slideNumber: 7 })}
            />
            <FeatureButton
              active={state.currentSlide === 8}
              icon={"menu-insights"}
              title={"Advanced reporting"}
              onClick={() => changeSlide({ slideNumber: 8 })}
            />
          </>
        </FeatureButtonsWrapper>
      </div>

      <div
        className={twJoin(
          "max-w-full",
          "px-8p",
          "768:px-16p",
          "1024:px-[unset]",
          "1024:max-w-[940px]",
          "1440:max-w-[1280px]",
          "gap-12p",
          "768:gap-22p",
          "1024:gap-24p",
          "1440:gap-32p",
          "mx-auto flex flex-col"
        )}
      >
        <Fade
          ref={textSliderRef}
          autoplay={false}
          arrows={false}
          defaultIndex={0}
          duration={10}
          transitionDuration={100}
          onChange={(from, to) => {
            changeSlide({ slideNumber: to, disableChangeSlide: true })
          }}
        >
          {textSlides.map((item, index) => (
            <div
              key={index}
              className={twJoin(
                "text-left text-14p leading-24p",
                "px-20p",
                "768:text-center",
                "1024:text-16p 1024:leading-24p font-400 text-neutral2-300"
              )}
            >
              {item.description}
            </div>
          ))}
        </Fade>
        <div
          className={"relative"}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          {state.smoothPosition && (
            <LearnMore
              className="fixed z-[5]"
              style={{
                top: state.smoothPosition.y - 34,
                left: state.smoothPosition.x - 72,
                pointerEvents: "none",
              }}
            />
          )}
          <Fade
            ref={sliderRef}
            autoplay={false}
            arrows={false}
            defaultIndex={0}
            duration={0}
            transitionDuration={400}
            onChange={(from, to) => {
              changeSlide({ slideNumber: to, disableChangeSlide: true })
            }}
          >
            {imagesSlides.map((item, index) => (
              <Link
                href={item.href}
                key={`${item.href}-${index}`}
                aria-label="Learn more"
              >
                <FeatureSlide>
                  <Image
                    className={"hidden 768:block"}
                    src={item.slide}
                    alt={item.alt}
                    preload={index === 0}
                    width={2288}
                    height={1136}
                    unoptimized
                  />
                  <Image
                    className={"w-full max-w-[660px] h-auto 768:hidden"}
                    src={item.slideMobile}
                    alt={item.alt}
                    preload={index === 0}
                    width={660}
                    height={1440}
                    unoptimized
                  />
                </FeatureSlide>
              </Link>
            ))}
          </Fade>
        </div>
      </div>
    </div>
  )
}
